import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspaceStore';

/** The one "Create workspace" entry (header switcher, Settings > Workspaces, plan picker): setup for a NEW workspace. */
export const NEW_WORKSPACE_PATH = '/onboarding?setup=new';

export function useCreateWorkspace() {
  const navigate = useNavigate();
  return () => navigate(NEW_WORKSPACE_PATH);
}

// Shared by the legacy WorkspaceSwitcher and the app shell switcher.
export function useWorkspaceSwitcher(close: () => void) {
  const navigate = useNavigate();
  const createWorkspace = useCreateWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const {
    workspaces,
    activeWorkspace,
    limits,
    fetchWorkspaces,
    fetchActiveWorkspace,
    fetchLimits,
    activateWorkspace,
  } = useWorkspaceStore();

  useEffect(() => {
    // Only fetch if authenticated (token exists)
    const token = localStorage.getItem('access_token');
    if (!token) {
      setInitialLoadDone(true);
      return;
    }

    const loadData = async () => {
      await Promise.all([
        fetchWorkspaces(),
        fetchActiveWorkspace(),
        fetchLimits(),
      ]);
      setInitialLoadDone(true);
    };
    loadData();
  }, []);

  const switchWorkspace = async (workspaceId: string) => {
    if (workspaceId === activeWorkspace?.id) {
      close();
      return;
    }

    setIsLoading(true);
    try {
      await activateWorkspace(workspaceId);
      close();
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNew = () => {
    close();
    createWorkspace();
  };

  const manageWorkspaces = () => {
    close();
    navigate('/settings/workspaces');
  };

  const canCreate = Boolean(limits?.can_create);

  return {
    workspaces,
    activeWorkspace,
    limits,
    isLoading,
    initialLoadDone,
    canCreate,
    switchWorkspace,
    createNew,
    manageWorkspaces,
  };
}
